import React from 'react';
import type {
  CollaborationPolicy,
  CollaborationPolicyFormState,
  RecipientPolicy
} from './collaborationControlPlaneShared';

interface CollaborationPolicyCardProps {
  canManage: boolean;
  policy: CollaborationPolicy | null;
  policyForm: CollaborationPolicyFormState;
  savingPolicy: boolean;
  policyLabel: Record<RecipientPolicy, string>;
  relativeTime: (value: string) => string;
  onRecipientPolicyChange: (value: RecipientPolicy) => void;
  onAllowUnregisteredViewChange: (value: boolean) => void;
  onRequireVerifiedEmailChange: (value: boolean) => void;
  onSavePolicy: () => void;
}

const CollaborationPolicyCard = ({
  canManage,
  policy,
  policyForm,
  savingPolicy,
  policyLabel,
  relativeTime,
  onRecipientPolicyChange,
  onAllowUnregisteredViewChange,
  onRequireVerifiedEmailChange,
  onSavePolicy
}: CollaborationPolicyCardProps): JSX.Element => (
  <div className="card">
    <h3>Who may receive supplier work</h3>
    <p className="muted">
      Choose how Edinshall Audit identifies an allowed recipient. This controls who
      may receive new supplier work; it does not activate a supplier or send anything.
    </p>
    {policy && (
      <div className="muted" style={{ marginBottom: '8px' }}>
        Last updated {relativeTime(policy.updatedAt)} {policy.updatedBy ? `by ${policy.updatedBy}` : ''}
      </div>
    )}
    <div style={{ display: 'grid', gap: '10px' }}>
      <label>
        <div className="muted" style={{ marginBottom: '4px' }}>Recipient policy</div>
        <select
          className="input"
          value={policyForm.recipientPolicy}
          onChange={(event) => onRecipientPolicyChange(event.target.value as RecipientPolicy)}
          disabled={!canManage}
        >
          <option value="registered_contacts_only">{policyLabel.registered_contacts_only}</option>
          <option value="verified_domain_any">{policyLabel.verified_domain_any}</option>
          <option value="named_delegates">{policyLabel.named_delegates}</option>
        </select>
        <span className="muted collaboration-field-help">
          {policyForm.recipientPolicy === 'registered_contacts_only' &&
            'Only active contacts recorded on a Supplier Profile may receive work.'}
          {policyForm.recipientPolicy === 'verified_domain_any' &&
            'Anyone using a verified supplier domain may receive work; a principal contact is not required for activation.'}
          {policyForm.recipientPolicy === 'named_delegates' &&
            'Only contacts named in the supplier authorisation rules may receive work.'}
        </span>
      </label>
      <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <input
          type="checkbox"
          checked={policyForm.allowUnregisteredView}
          onChange={(event) => onAllowUnregisteredViewChange(event.target.checked)}
          disabled={!canManage}
        />
        Allow an approved recipient to view work before completing registration
      </label>
      <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <input
          type="checkbox"
          checked={policyForm.requireVerifiedEmail}
          onChange={(event) => onRequireVerifiedEmailChange(event.target.checked)}
          disabled={!canManage}
        />
        Require the recipient's email address to be verified
      </label>
      <div>
        <button className="btn" type="button" onClick={onSavePolicy} disabled={!canManage || savingPolicy}>
          {savingPolicy ? 'Saving…' : 'Save policy'}
        </button>
      </div>
    </div>
  </div>
);

export default CollaborationPolicyCard;
